import React from 'react';
import {
  UsersIcon,
  ServerIcon,
  LinkIcon,
  BoltIcon,
  ClockIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useSystemOverview } from '../../hooks/admin/useAdminStats';
import StatCard from '../../components/common/StatCard';
import StatusBadge from '../../components/common/StatusBadge';
import LoadingSpinner from '../../components/LoadingSpinner';
import ErrorMessage from '../../components/ErrorMessage';

const AdminDashboard = () => {
  const { data: systemData, loading: systemLoading, error: systemError } = useSystemOverview();

  // Mock data for tickets and platform status (would be replaced with real hooks)
  const recentTickets = [
    {
      id: 'TKT-001',
      subject: 'Unable to connect Instagram account',
      user: 'Sarah Johnson',
      priority: 'high',
      status: 'open',
      created: '2024-03-20T09:30:00Z',
      category: 'Platform Connection'
    },
    {
      id: 'TKT-002',
      subject: 'API rate limit questions',
      user: 'Mike Chen',
      priority: 'medium',
      status: 'in-progress',
      created: '2024-03-19T14:15:00Z',
      category: 'API'
    },
    {
      id: 'TKT-003',
      subject: 'Report generation timeout',
      user: 'Emma Wilson',
      priority: 'low',
      status: 'resolved',
      created: '2024-03-18T11:45:00Z',
      category: 'Reports'
    }
  ];

  const apiErrors = [
    {
      endpoint: '/api/v1/metrics/overview',
      count: 23,
      rate: '0.02%',
      lastOccurred: '2024-03-20T10:15:00Z'
    },
    {
      endpoint: '/api/v1/platforms/connect',
      count: 45,
      rate: '0.08%',
      lastOccurred: '2024-03-20T09:45:00Z'
    },
    {
      endpoint: '/api/v1/reports/generate',
      count: 12,
      rate: '0.01%',
      lastOccurred: '2024-03-20T08:30:00Z'
    }
  ];

  const getTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 60) {
      return `${diffInMinutes} minutes ago`;
    } else if (diffInMinutes < 1440) {
      return `${Math.floor(diffInMinutes / 60)} hours ago`;
    } else {
      return `${Math.floor(diffInMinutes / 1440)} days ago`;
    }
  };

  if (systemLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">System Overview</h1>
        <p className="text-gray-600">Monitor system health, user activity, and platform performance</p>
      </div>

      {/* System Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {systemError ? (
          <div className="col-span-full">
            <ErrorMessage message={systemError} />
          </div>
        ) : systemData ? (
          <>
            <StatCard
              title="Total Users"
              value={systemData.totalUsers?.toLocaleString() || '12,847'}
              change="+12.5%"
              icon={UsersIcon}
              iconColor="text-primary-600"
              iconBgColor="bg-primary-100"
            />
            <StatCard
              title="Active Users (30d)"
              value={systemData.activeUsers?.toLocaleString() || '8,923'}
              change="Active"
              icon={ServerIcon}
              iconColor="text-success-600"
              iconBgColor="bg-success-100"
            />
            <StatCard
              title="Platform Connections"
              value={systemData.totalConnections?.toLocaleString() || '45,632'}
              change="+8.3%"
              icon={LinkIcon}
              iconColor="text-admin-600"
              iconBgColor="bg-admin-100"
            />
            <StatCard
              title="API Calls (24h)"
              value={systemData.apiCalls24h || '2.4M'}
              change="24h"
              icon={BoltIcon}
              iconColor="text-warning-600"
              iconBgColor="bg-warning-100"
            />
            <StatCard
              title="Avg Response Time"
              value={systemData.avgResponseTime || '142ms'}
              change="Good"
              icon={ClockIcon}
              iconColor="text-success-600"
              iconBgColor="bg-success-100"
            />
            <StatCard
              title="System Uptime (30d)"
              value={systemData.uptime || '99.98%'}
              change="Operational"
              icon={ServerIcon}
              iconColor="text-success-600"
              iconBgColor="bg-success-100"
            />
          </>
        ) : (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <LoadingSpinner />
            </div>
          ))
        )}
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Support Tickets */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Recent Support Tickets</h3>
            <button className="text-admin-600 hover:text-admin-700 text-sm font-medium">
              View All
            </button>
          </div>
          <div className="space-y-4">
            {recentTickets.map(ticket => (
              <div
                key={ticket.id}
                className={`p-4 rounded-lg border-l-4 ${
                  ticket.priority === 'high' ? 'border-danger-500 bg-danger-50' :
                  ticket.priority === 'medium' ? 'border-warning-500 bg-warning-50' :
                  'border-success-500 bg-success-50'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-gray-900">#{ticket.id}</span>
                    <StatusBadge status={ticket.status} type="ticket" />
                    <StatusBadge status={ticket.priority} type="priority" />
                  </div>
                </div>
                <h4 className="font-medium text-gray-900 mb-1">{ticket.subject}</h4>
                <p className="text-sm text-gray-600 mb-2">
                  {ticket.user} • {ticket.category}
                </p>
                <p className="text-xs text-gray-500">
                  Created {getTimeAgo(ticket.created)}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* API Error Monitoring */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">API Error Monitoring</h3>
            <button className="text-admin-600 hover:text-admin-700 text-sm font-medium">
              View Details
            </button>
          </div>
          <div className="space-y-4">
            {apiErrors.map(error => (
              <div key={error.endpoint} className="p-4 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors">
                <div className="flex items-center justify-between mb-2">
                  <code className="text-sm font-mono text-gray-900 bg-gray-100 px-2 py-1 rounded">
                    {error.endpoint}
                  </code>
                  <div className="flex items-center space-x-2">
                    <ExclamationTriangleIcon className="w-4 h-4 text-warning-500" />
                    <span className="text-sm font-medium text-gray-900">{error.count} errors</span>
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <span>Error rate: {error.rate}</span>
                  <span>Last: {getTimeAgo(error.lastOccurred)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button className="p-4 text-center border border-gray-200 rounded-lg hover:border-admin-500 hover:bg-admin-50 transition-colors">
            <UsersIcon className="w-8 h-8 text-admin-600 mx-auto mb-2" />
            <span className="text-sm font-medium text-gray-900">Manage Users</span>
          </button>
          <button className="p-4 text-center border border-gray-200 rounded-lg hover:border-admin-500 hover:bg-admin-50 transition-colors">
            <ServerIcon className="w-8 h-8 text-admin-600 mx-auto mb-2" />
            <span className="text-sm font-medium text-gray-900">System Health</span>
          </button>
          <button className="p-4 text-center border border-gray-200 rounded-lg hover:border-admin-500 hover:bg-admin-50 transition-colors">
            <BoltIcon className="w-8 h-8 text-admin-600 mx-auto mb-2" />
            <span className="text-sm font-medium text-gray-900">API Monitor</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
